// KWHeroSearch.jsx (herosearch)
import React, { useCallback, useMemo, useState } from "react";

/** === Keep your constants === */
const LISTINGS_BASE = "https://kentuckyhomeliving.kw.com/search/sale";
const KW_LOCATION_BASE = "https://kentuckyhomeliving.kw.com/search/location";

/** === DATA (reuse same arrays) === */
const FEATURED_CITIES = [
  { name: "Florence" },
  { name: "Fort Thomas" },
  { name: "Union" },
  { name: "Covington" },
  { name: "Newport" },
  { name: "Independence" },
];

const ZIP_LOCATIONS = [
  {
    zip: "41001",
    token:
      "ChIJJ7kKjJ-jQYgRjGCrCZ7Ia8Q-0.5338605285365372,41001,Alexandria%2C%20KY%2041001%2C%20USA",
    viewport: "39.00429889999999,-84.32677,38.8156108,-84.46475389999999",
  },
  // ... keep the rest of your ZIP_LOCATIONS here
];

const SCHOOL_DISTRICTS = [
  {
    name: "Beechwood Independent",
    slug: "Beechwood-Independent-School-District-Fort-Mitchell-KY-USA",
    id: "918753",
    q: "Beechwood Independent School District, Fort Mitchell, KY, USA",
    viewport: "39.0668247120808,-84.52153201562498,39.02536250700113,-84.58744998437498",
  },
  // ... keep the rest of your SCHOOL_DISTRICTS here
];

/** === URL HELPERS === */
function openNewTab(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

// ✅ Universal KW search URL (no hardcoded cities needed)
function buildKwSearchUrl(q, viewport) {
  const query = String(q || "").trim();
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (viewport) params.set("viewport", viewport);
  return `${LISTINGS_BASE}?${params.toString()}`; // IMPORTANT: no "/?"
}

function buildKwEntityUrl({ slug, id, q, viewport }) {
  const base = `${LISTINGS_BASE}/${slug}/${id}`;
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (viewport) params.set("viewport", viewport);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function buildZipLocationUrl({ token, viewport, zoom }) {
  const params = new URLSearchParams();
  if (viewport) params.set("viewport", viewport);
  if (zoom) params.set("zoom", String(zoom));
  const qs = params.toString();
  return `${KW_LOCATION_BASE}/${token}${qs ? `?${qs}` : ""}`;
}

function normalizeQuery(input) {
  return String(input || "").trim();
}

/**
 * ✅ Backend viewport lookup (preferred when backend is reachable)
 * Expects: GET /api/kw/viewport?q=<place>
 * Returns: { viewport: "north,east,south,west", q?: "Formatted Place, ..." }
 */
async function fetchKwViewport(placeQuery) {
  try {
    const r = await fetch(`/api/kw/viewport?q=${encodeURIComponent(placeQuery)}`);
    if (!r.ok) return null;
    const data = await r.json();
    return {
      viewport: data?.viewport || null,
      q: data?.q || null,
    };
  } catch {
    return null;
  }
}

/**
 * ✅ Client-side fallback viewport lookup (works even if backend is down)
 * Biases results toward Northern KY / Cincinnati region without hard-locking KY.
 *
 * Put your key in .env as:
 * REACT_APP_GOOGLE_MAPS_API_KEY=xxxx
 */
async function getViewportFromGeocode(placeText) {
  const text = String(placeText || "").trim();
  if (!text) return null;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return null;

  const address = encodeURIComponent(text);

  // Bias: Northern KY / Cincinnati metro (adjust if you want)
  const bounds = encodeURIComponent("38.55,-85.10|39.35,-84.20");
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&bounds=${bounds}&region=us&key=${key}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    const result = data?.results?.[0];
    const viewport = result?.geometry?.viewport;

    if (!viewport?.northeast || !viewport?.southwest) return null;

    const ne = viewport.northeast;
    const sw = viewport.southwest;

    return {
      viewport: `${ne.lat},${ne.lng},${sw.lat},${sw.lng}`,
      q: result?.formatted_address || text,
    };
  } catch {
    return null;
  }
}

/**
 * Props let you customize the secondary button behavior on LandingPage
 */
export default function KWHeroSearch({
  placeholder = "Search city, ZIP, school district, or keyword (ex: 41071, Beechwood, Florence)",
  primaryLabel = "Search Listings",
  secondaryLabel = "Browse Cities",
  onSecondaryClick,
  className = "",
}) {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const onSubmitSearch = useCallback(async () => {
    const q = normalizeQuery(query);

    if (!q) {
      openNewTab(buildKwSearchUrl("Northern Kentucky"));
      return;
    }

    setIsSearching(true);
    try {
      // 1) ZIP exact match -> KW location token URL
      if (/^\d{5}$/.test(q)) {
        const zipHit = ZIP_LOCATIONS.find((z) => z.zip === q);
        if (zipHit) {
          openNewTab(buildZipLocationUrl(zipHit));
          return;
        }
      }

      const qL = q.toLowerCase();

      // 2) School district contains match -> entity URL
      const districtHit = SCHOOL_DISTRICTS.find((d) => d.name.toLowerCase().includes(qL));
      if (districtHit) {
        openNewTab(
          districtHit.slug && districtHit.id
            ? buildKwEntityUrl({
                slug: districtHit.slug,
                id: districtHit.id,
                q: districtHit.q,
                viewport: districtHit.viewport,
              })
            : buildKwSearchUrl(districtHit.qFallback || districtHit.name)
        );
        return;
      }

      // 3) Universal: use viewport lookup for ANY place text (no city hardcoding)
      const backend = await fetchKwViewport(q);
      let viewport = backend?.viewport || null;
      let finalQ = backend?.q || null;

      if (!viewport) {
        const geo = await getViewportFromGeocode(q);
        viewport = geo?.viewport || null;
        finalQ = geo?.q || finalQ || q;
      }

      // 4) Universal KW search URL (works everywhere)
      openNewTab(buildKwSearchUrl(finalQ || q, viewport));
    } finally {
      setIsSearching(false);
    }
  }, [query]);

  const resultsLabel = useMemo(() => {
    const q = query.trim();
    if (!q) return "Search by city, ZIP, school district, or keyword.";

    const qL = q.toLowerCase();
    const cityMatches = FEATURED_CITIES.filter(
      (c) => qL.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(qL)
    ).length;

    const districtMatches = SCHOOL_DISTRICTS.filter((d) => d.name.toLowerCase().includes(qL)).length;
    const zipExact = /^\d{5}$/.test(q) && ZIP_LOCATIONS.some((z) => z.zip === q);

    if (zipExact) return `ZIP match: ${q}`;

    if (districtMatches || cityMatches) {
      const parts = [];
      if (districtMatches) parts.push(`${districtMatches} school match${districtMatches === 1 ? "" : "es"}`);
      if (cityMatches) parts.push(`${cityMatches} featured cit${cityMatches === 1 ? "y" : "ies"}`);
      return `${parts.join(" • ")} • Press Enter to search`;
    }

    return `Press Enter to search KW for “${q}”`;
  }, [query]);

  return (
    <div className={`nb-search ${className}`}>
      <div className="nb-search-field">
        <span className="nb-search-icon" aria-hidden="true">
          ⌕
        </span>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmitSearch();
          }}
          placeholder={placeholder}
          type="text"
          disabled={isSearching}
        />
      </div>

      <div className="nb-search-actions">
        <button type="button" className="nb-search-cta" onClick={onSubmitSearch} disabled={isSearching}>
          {isSearching ? "Searching..." : primaryLabel}
        </button>

        <button
          type="button"
          className="nb-search-cta secondary"
          onClick={onSecondaryClick || (() => openNewTab(buildKwSearchUrl("Northern Kentucky")))}
          disabled={isSearching}
        >
          {secondaryLabel}
        </button>
      </div>
    </div>
  );
}
